import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Box, Backdrop } from "@mui/material";
import { useForm, FormProvider } from 'react-hook-form';
import { DatePickerFormControl } from "../common/form/DatePickerFormControl";
import { MedicalJournalFormat } from "../../models/enums/medicalJournalFormat";
import { medicalJournalsService } from "../../services/medicalJournalsService";
import CompanyAutocompleteFormControl from "../common/form/CompanyAutocompleteFormControl";
import TerminalsAutocompleteFormControl from "../common/form/TerminalsAutocompleteFormControl";
import { LookupDto } from '../../models/common/lookup.dto';
import ErrorAlert from '../common/ErrorAlert';
import Spinner from '../common/Spinner';
import TmEmptyButton from '../common/buttons/TmEmptyButton';
import TmFullButton from '../common/buttons/TmFullButton';
import { DateUtils } from '../../helpers/dateUtils';
import dayjs from 'dayjs';

interface CreateJournalDialogProps {
    open: boolean;
    onClose: () => void;
}

interface CreateJournalForm {
    fromDate: Date | null;
    toDate: Date | null;
    company: any;
    terminals: LookupDto[];
    format: MedicalJournalFormat;
}

export const CreateJournalDialog: React.FC<CreateJournalDialogProps> = ({ open, onClose }) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const maxPeriodDays = 31;
    const periodTooLongMessage = 'Максимальный период выборки для журнала - 31 день. Уменьшите период выборки. ';

    const methods = useForm<CreateJournalForm>({
        defaultValues: {
            fromDate: null,
            toDate: null,
            company: null,
            terminals: [],
            format: MedicalJournalFormat.Xlsx,
        },
    });

    const company = methods.watch('company');
    const fromDate = methods.watch('fromDate');
    const toDate = methods.watch('toDate');

    const isPeriodTooLong = useMemo(() => {
        if (!fromDate || !toDate) return false;
        const from = dayjs(fromDate).startOf('day');
        const to = dayjs(toDate).startOf('day');
        if (!from.isValid() || !to.isValid()) return false;
        if (to.isBefore(from)) return false;
        const inclusiveDays = to.diff(from, 'day') + 1;
        return inclusiveDays > maxPeriodDays;
    }, [fromDate, toDate]);

    useEffect(() => {
        const fromManualError = methods.formState.errors.fromDate?.type === 'manual';
        const toManualError = methods.formState.errors.toDate?.type === 'manual';

        if (fromDate && toDate && isPeriodTooLong) {
            methods.setError('fromDate', { type: 'manual', message: periodTooLongMessage });
            methods.setError('toDate', { type: 'manual', message: periodTooLongMessage });
            return;
        }

        if (fromManualError) methods.clearErrors('fromDate');
        if (toManualError) methods.clearErrors('toDate');
    }, [fromDate, toDate, isPeriodTooLong, methods]);

    const handleSubmit = methods.handleSubmit((data) => {
        setLoading(true);
        medicalJournalsService.create({
            from: DateUtils.dateToISO(data.fromDate),
            to: DateUtils.dateToISO(data.toDate),
            companyId: data.company!.id!,
            format: data.format!,
            terminalIds: data.terminals && data.terminals.length > 0 ? data.terminals.map(t => t.id) : undefined,
        })
            .then(response => {
                methods.reset();
                onClose();
            })
            .catch(e => {
                console.log(e);
                const error = e.response?.data?.message ?? e.message;
                setError(error);
            })
            .finally(() => setLoading(false));
    });

    const handleClose = () => {
        methods.reset();
        setError('');
        onClose();
    };

    return (
        <Box>
            <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: "25px" } }}>
                <DialogTitle variant='h4' sx={{ textAlign: 'center' }}>Формирование журнала медосмотров</DialogTitle>
                <DialogContent>
                    <FormProvider {...methods}>
                        <Box component="form" noValidate sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <DatePickerFormControl
                                        name="fromDate"
                                        label="Начало периода"
                                        required
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <DatePickerFormControl
                                        name="toDate"
                                        label="Конец периода"
                                        required
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CompanyAutocompleteFormControl
                                        name="company"
                                        required
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TerminalsAutocompleteFormControl
                                        name="terminals"
                                        companyId={company?.id}
                                        label="Терминал"
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset">
                                        <RadioGroup 
                                            row 
                                            value={methods.watch('format')} 
                                            onChange={(e) => methods.setValue('format', e.target.value as MedicalJournalFormat)}
                                        >
                                            <FormControlLabel disabled value={MedicalJournalFormat.Pdf} control={<Radio />} label="PDF" />
                                            <FormControlLabel disabled value={MedicalJournalFormat.Docx} control={<Radio />} label="DOCX" />
                                            <FormControlLabel value={MedicalJournalFormat.Xlsx} control={<Radio />} label="XLSX" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </FormProvider>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center', paddingBottom: "2em" }}>
                    <TmEmptyButton onClick={handleClose} width='45%'>
                        Отмена
                    </TmEmptyButton>
                    <TmFullButton onClick={handleSubmit} width='45%' disabled={loading || !fromDate || !toDate || isPeriodTooLong}>
                        Сформировать
                    </TmFullButton>
                </DialogActions>
                {error && (
                    <ErrorAlert
                        message={error}
                        onClose={() => setError('')}
                    />
                )}
            </Dialog>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={loading}
            >
                <Spinner text="Формирование журнала..." />
            </Backdrop>
        </Box>
    );
};

export default CreateJournalDialog;
